import { CustomerContext } from './context/CustomerContext';
import ProductReviewsForm from './ProductReviewsForm';
import ProductReviewsList from './ProductReviewsList';
import ProductReviewsSummary from './ProductReviewsSummary';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import '../styles/ProductReviews.css';


/**
 * Component for displaying a list of product reviews and a form for
 * submitting a review.
 * @component
 */
const ProductReviews = ({ product }) => {
    /** Contexts */
    const { customerData } = useContext(CustomerContext);
    /** States */
    const [ isShown, setIsShown ] = useState(false);
    const [ showFeedback, setShowFeedback ] = useState(false);

    /** Event Handlers */
    const handleWriteAReview = () => {
        if (Object.keys(customerData).length === 0) {
            setShowFeedback(true);
            setIsShown(false);
            return;
        }
        setIsShown((i) => !i);
    };

    return <section
        className={'product-reviews--container'}>
        <h2>Product Reviews</h2>
        <hr />

        <ProductReviewsSummary
            averageRating={product.avgRating}
            reviewsPerRating={[...Array(5).keys()]
                .map((index) => ({
                    starsRating: index + 1,
                    reviewCount: product.Reviews.filter(
                        (review) => review.starsRating === index + 1
                    ).length,
                }))
            }
            totalReviews={product.totalReviews}
        />
        <div
            className={'mb-3'}>
            <ProductReviewsList
                productId={product.id}
            />
        </div>
        <div
            className={'product-reviews--toggle-container'}>
            <button
                className={'product-reviews--toggle-button'}
                onClick={handleWriteAReview}>
                Write a review
            </button>
            <p
                className={`text-danger${showFeedback ? '' : ' d-none'}`}>
                You need to be logged in to submit a review.
            </p>
        </div>
        <div
            className={`my-3${isShown === true ? '' : ' d-none'}`}>
            <ProductReviewsForm
                productId={product.id}
            />
        </div>
    </section>;
};

/** Data Types of Properties */
ProductReviews.propTypes = {
    /** The product state from the product. */
    product: PropTypes.shape({
        /** The product ID. */
        id: PropTypes.number.isRequired,
        /** The average rating of all the reviews for the product. */
        avgRating: PropTypes.number,
        /** The total number of reviews for the product. */
        totalReviews: PropTypes.number,
        /** The reviews for the product. */
        Reviews: PropTypes.arrayOf(PropTypes.shape({
            /** The product rating represented by the number of stars. */
            starsRating: PropTypes.number.isRequired,
            /** The title of the comment left by the customer. */
            title: PropTypes.string,
            /** The comment left by the customer. */
            comment: PropTypes.string,
            /** The customer data. */
            Customer: PropTypes.shape({
                /** The user data associated to the customer. */
                User: PropTypes.shape({
                    /** The first name of the customer who left the review. */
                    firstName: PropTypes.string,
                    /** The last name of the customer who left the review. */
                    lastName: PropTypes.string,
                })
            }),
        })),
    }),
};

/** Default Properties */
ProductReviews.defaultProps = {
    reviews: [],
};

export default ProductReviews;
